import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useApi } from '../hooks/useApi';
import { useAuth, isGlobalAdmin } from '../hooks/useAuth';
import { useTheme } from '../hooks/useTheme';
import StatsSummaryCards from '../components/StatsSummaryCards';
import { getCRRatioColor } from '../utils/format';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

// --- Interfaces (same as Dashboard.tsx) ---

interface SprintDataPoint {
  sprintId: string;
  sprintName: string;
  piName: string;
  costs: number;
  revenue: number;
  costOverRevenue: number;
}

interface TeamSummary {
  teamId: string;
  teamName: string;
  projectId: string;
  projectName: string;
  totalCosts: number;
  totalRevenue: number;
  costOverRevenue: number;
  sprintData: SprintDataPoint[];
}

interface DashboardSummary {
  year: number;
  teams: TeamSummary[];
}

interface ProjectSummary {
  projectId: string;
  projectName: string;
  teams: TeamSummary[];
  totalCosts: number;
  totalRevenue: number;
  costOverRevenue: number;
  sprintData: SprintDataPoint[];
}

interface TokenValidationResponse {
  valid: boolean;
  tokenName: string;
  dashboard: DashboardSummary;
}

// --- Slide types ---

type Slide =
  | { type: 'overview' }
  | { type: 'project-teams'; projectIndex: number; teamStart: number; teamEnd: number };

// --- Helpers (same as Dashboard.tsx) ---

function aggregateByProject(teams: TeamSummary[]): ProjectSummary[] {
  const projectMap = new Map<string, ProjectSummary>();

  for (const team of teams) {
    if (!projectMap.has(team.projectId)) {
      projectMap.set(team.projectId, {
        projectId: team.projectId,
        projectName: team.projectName,
        teams: [],
        totalCosts: 0,
        totalRevenue: 0,
        costOverRevenue: 0,
        sprintData: [],
      });
    }

    const project = projectMap.get(team.projectId)!;
    project.teams.push(team);
    project.totalCosts += team.totalCosts;
    project.totalRevenue += team.totalRevenue;
  }

  for (const project of projectMap.values()) {
    const sprintMap = new Map<string, SprintDataPoint>();

    for (const team of project.teams) {
      for (const sprint of team.sprintData) {
        if (!sprintMap.has(sprint.sprintId)) {
          sprintMap.set(sprint.sprintId, {
            sprintId: sprint.sprintId,
            sprintName: sprint.sprintName,
            piName: sprint.piName,
            costs: 0,
            revenue: 0,
            costOverRevenue: 0,
          });
        }
        const aggregated = sprintMap.get(sprint.sprintId)!;
        aggregated.costs += sprint.costs;
        aggregated.revenue += sprint.revenue;
      }
    }

    for (const sprint of sprintMap.values()) {
      sprint.costOverRevenue = sprint.revenue > 0 ? sprint.costs / sprint.revenue : 0;
    }

    project.sprintData = Array.from(sprintMap.values()).sort((a, b) =>
      a.sprintName.localeCompare(b.sprintName)
    );

    project.costOverRevenue = project.totalRevenue > 0 ? project.totalCosts / project.totalRevenue : 0;
  }

  return Array.from(projectMap.values()).sort((a, b) => a.projectName.localeCompare(b.projectName));
}

function buildSlides(projects: ProjectSummary[]): Slide[] {
  const slides: Slide[] = [{ type: 'overview' }];
  const TEAMS_PER_SLIDE = 4;
  projects.forEach((project, pi) => {
    for (let start = 0; start < project.teams.length; start += TEAMS_PER_SLIDE) {
      const end = Math.min(start + TEAMS_PER_SLIDE, project.teams.length);
      slides.push({ type: 'project-teams', projectIndex: pi, teamStart: start, teamEnd: end });
    }
  });
  return slides;
}

const SLIDE_INTERVAL = 10000;
const REFETCH_INTERVAL = 5 * 60 * 1000;
const currentYear = new Date().getFullYear();

function getApiUrl(): string {
  if (import.meta.env.DEV && !import.meta.env.VITE_USER_POOL_ID) {
    return 'http://localhost:3001';
  }
  const config = (window as unknown as { appConfig: { apiUrl: string } }).appConfig;
  return config?.apiUrl || 'http://localhost:3001';
}

// --- Main Component ---

export default function Presentation() {
  const navigate = useNavigate();
  const api = useApi();
  const { user, isLoading: authLoading } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  // Token-based auth state
  const [tokenInput, setTokenInput] = useState('');
  const [storedToken, setStoredToken] = useState<string | null>(() =>
    sessionStorage.getItem('presentation-token')
  );
  const [tokenError, setTokenError] = useState('');
  const [tokenValidating, setTokenValidating] = useState(false);

  const isAdmin = isGlobalAdmin(user);
  const useTokenMode = !authLoading && !isAdmin;

  // Force dark mode on mount, restore on unmount
  useEffect(() => {
    const originalTheme = theme;
    if (theme !== 'dark') {
      toggleTheme();
    }
    return () => {
      if (originalTheme === 'light') {
        toggleTheme();
      }
    };
    // Only run on mount/unmount
  }, []);

  // Authenticated admin query
  const adminQuery = useQuery({
    queryKey: ['dashboard', currentYear],
    queryFn: () => api.get<DashboardSummary>(`/api/dashboard/summary?year=${currentYear}`),
    refetchInterval: REFETCH_INTERVAL,
    enabled: isAdmin,
  });

  // Token-based query
  const tokenQuery = useQuery({
    queryKey: ['presentation-token-data', storedToken, currentYear],
    queryFn: async () => {
      const resp = await fetch(`${getApiUrl()}/api/presentation-tokens/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: storedToken, year: currentYear }),
      });
      if (!resp.ok) {
        // Token invalid — clear it
        sessionStorage.removeItem('presentation-token');
        setStoredToken(null);
        throw new Error('Invalid token');
      }
      return resp.json() as Promise<TokenValidationResponse>;
    },
    refetchInterval: REFETCH_INTERVAL,
    enabled: useTokenMode && !!storedToken,
  });

  // Resolve data from whichever mode is active
  const data: DashboardSummary | undefined = isAdmin
    ? adminQuery.data
    : tokenQuery.data?.dashboard;
  const isLoading = isAdmin ? adminQuery.isLoading : tokenQuery.isLoading;
  const queryError = isAdmin ? adminQuery.error : tokenQuery.error;

  const teams = data?.teams || [];
  const projects = useMemo(() => aggregateByProject(teams), [teams]);
  const slides = useMemo(() => buildSlides(projects), [projects]);

  // Grand totals for overview
  const grandTotalCosts = projects.reduce((sum, p) => sum + p.totalCosts, 0);
  const grandTotalRevenue = projects.reduce((sum, p) => sum + p.totalRevenue, 0);
  const grandCR = grandTotalRevenue > 0 ? grandTotalCosts / grandTotalRevenue : 0;

  // Auto-cycle slides (pauses when isPaused)
  useEffect(() => {
    if (slides.length <= 1 || isPaused) return;
    const interval = setInterval(() => {
      setCurrentSlideIndex((prev) => (prev + 1) % slides.length);
    }, SLIDE_INTERVAL);
    return () => clearInterval(interval);
  }, [slides.length, isPaused]);

  // Reset slide index if slides change
  useEffect(() => {
    setCurrentSlideIndex(0);
  }, [slides.length]);

  // Keyboard controls
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        navigate('/');
      } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        setCurrentSlideIndex((prev) => (prev + 1) % slides.length);
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        setCurrentSlideIndex((prev) => (prev - 1 + slides.length) % slides.length);
      } else if (e.key === ' ') {
        e.preventDefault();
        setIsPaused((prev) => !prev);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [navigate, slides.length]);

  // Handle token submission
  const handleTokenSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const token = tokenInput.trim();
    if (!token) return;

    setTokenError('');
    setTokenValidating(true);

    try {
      const resp = await fetch(`${getApiUrl()}/api/presentation-tokens/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, year: currentYear }),
      });

      if (!resp.ok) {
        setTokenError('Invalid token. Please check and try again.');
        return;
      }

      // Store token for re-validation
      sessionStorage.setItem('presentation-token', token);
      setStoredToken(token);
      setTokenInput('');
    } catch {
      setTokenError('Failed to validate token. Please try again.');
    } finally {
      setTokenValidating(false);
    }
  };

  // Auth loading
  if (authLoading) {
    return (
      <div className="fixed inset-0 bg-dark-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-2 border-primary-500 border-t-transparent"></div>
      </div>
    );
  }

  // Token mode: show token input if no stored token
  if (useTokenMode && !storedToken) {
    return (
      <div className="fixed inset-0 bg-dark-900 flex items-center justify-center">
        <div className="w-full max-w-md mx-4">
          <div className="text-center mb-8">
            <svg className="w-16 h-16 text-primary-500 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <h1 className="text-2xl font-bold text-white mb-2">Presentation Mode</h1>
            <p className="text-gray-400">Enter your access token to start the presentation.</p>
          </div>

          <form onSubmit={handleTokenSubmit} className="space-y-4">
            <div>
              <input
                type="password"
                value={tokenInput}
                onChange={(e) => setTokenInput(e.target.value)}
                placeholder="Paste your access token"
                className="w-full px-4 py-3 bg-dark-700 border border-dark-500 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                autoFocus
              />
              {tokenError && (
                <p className="text-primary-400 text-sm mt-2">{tokenError}</p>
              )}
            </div>
            <button
              type="submit"
              disabled={tokenValidating || !tokenInput.trim()}
              className="w-full py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {tokenValidating ? 'Validating...' : 'Start Presentation'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => navigate('/login')}
              className="text-sm text-gray-500 hover:text-gray-300 transition-colors"
            >
              Sign in with account instead
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-dark-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-2 border-primary-500 border-t-transparent"></div>
      </div>
    );
  }

  if (queryError) {
    return (
      <div className="fixed inset-0 bg-dark-900 flex items-center justify-center">
        <div className="text-center">
          <p className="text-primary-400 text-lg mb-4">Failed to load presentation data</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (projects.length === 0) {
    return (
      <div className="fixed inset-0 bg-dark-900 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-400 text-lg mb-4">No data available for {currentYear}</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const currentSlide = slides[currentSlideIndex] || slides[0];

  return (
    <div className="fixed inset-0 bg-dark-900 text-white overflow-hidden">
      {/* Progress bar */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-dark-700">
        <div
          className="h-full bg-primary-500 transition-all duration-500"
          style={{ width: `${((currentSlideIndex + 1) / slides.length) * 100}%` }}
        />
      </div>

      {/* Slide content */}
      <div className="h-full flex flex-col px-12 py-8 pt-12">
        {currentSlide.type === 'overview' && (
          <OverviewSlide
            year={currentYear}
            projects={projects}
            totalCosts={grandTotalCosts}
            totalRevenue={grandTotalRevenue}
            costOverRevenue={grandCR}
          />
        )}
        {currentSlide.type === 'project-teams' && (
          <ProjectTeamsSlide
            project={projects[currentSlide.projectIndex]}
            teams={projects[currentSlide.projectIndex].teams.slice(currentSlide.teamStart, currentSlide.teamEnd)}
            year={currentYear}
          />
        )}
      </div>

      {/* Slide controls & indicator */}
      <div className="absolute bottom-4 left-0 right-0 flex items-center justify-center gap-3">
        <button
          onClick={() => setCurrentSlideIndex((prev) => (prev - 1 + slides.length) % slides.length)}
          className="text-dark-500 hover:text-gray-300 transition-colors p-1"
          aria-label="Previous slide"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <button
          onClick={() => setIsPaused((prev) => !prev)}
          className="text-dark-500 hover:text-gray-300 transition-colors p-1"
          aria-label={isPaused ? 'Play' : 'Pause'}
        >
          {isPaused ? (
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M8 5v14l11-7z" />
            </svg>
          ) : (
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
            </svg>
          )}
        </button>
        <button
          onClick={() => setCurrentSlideIndex((prev) => (prev + 1) % slides.length)}
          className="text-dark-500 hover:text-gray-300 transition-colors p-1"
          aria-label="Next slide"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </button>
        <div className="flex gap-1.5 ml-1">
          {slides.map((_, i) => (
            <div
              key={i}
              className={`w-2 h-2 rounded-full transition-colors ${
                i === currentSlideIndex ? 'bg-primary-500' : 'bg-dark-600'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Branding */}
      <div className="absolute bottom-4 right-6 text-xs text-dark-500 uppercase tracking-wider font-medium">
        Performance Reporting
      </div>
    </div>
  );
}

// --- Slide Components ---

function OverviewSlide({
  year,
  projects,
  totalCosts,
  totalRevenue,
  costOverRevenue,
}: {
  year: number;
  projects: ProjectSummary[];
  totalCosts: number;
  totalRevenue: number;
  costOverRevenue: number;
}) {
  return (
    <div className="flex-1 flex flex-col">
      <h1 className="text-4xl font-bold mb-8">
        Performance Overview <span className="text-primary-500">{year}</span>
      </h1>

      <div className="mb-8">
        <StatsSummaryCards totalCosts={totalCosts} totalRevenue={totalRevenue} costOverRevenue={costOverRevenue} />
      </div>

      <div className="flex-1 grid grid-cols-2 lg:grid-cols-3 gap-4 auto-rows-min">
        {projects.map((project) => (
          <div key={project.projectId} className="bg-dark-700/50 border border-dark-600 rounded-xl p-5">
            <h3 className="text-lg font-semibold text-white mb-1">
              {project.projectName}
            </h3>
            <p className="text-sm text-gray-400 mb-3">
              {project.teams.length} team{project.teams.length !== 1 ? 's' : ''}
            </p>
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-lg bg-gradient-to-br from-primary-500/20 to-primary-600/10 border border-primary-500/30 p-2.5">
                <span className="text-xs font-medium text-primary-400 uppercase tracking-wide">Costs</span>
                <p className="text-base font-bold text-white mt-0.5">
                  {project.totalCosts.toLocaleString('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })}
                </p>
              </div>
              <div className="rounded-lg bg-gradient-to-br from-accent-aquamarine/20 to-accent-turquoise/10 border border-accent-aquamarine/30 p-2.5">
                <span className="text-xs font-medium text-accent-turquoise uppercase tracking-wide">Revenue</span>
                <p className="text-base font-bold text-white mt-0.5">
                  {project.totalRevenue.toLocaleString('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })}
                </p>
              </div>
              <div className="rounded-lg bg-gradient-to-br from-accent-blue/20 to-accent-blue/10 border border-accent-blue/30 p-2.5">
                <span className="text-xs font-medium text-accent-blue uppercase tracking-wide">C/R Ratio</span>
                <p className="text-base font-bold mt-0.5" style={{ color: getCRRatioColor(project.costOverRevenue) }}>
                  {project.costOverRevenue.toFixed(2)}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ProjectTeamsSlide({
  project,
  teams,
  year,
}: {
  project: ProjectSummary;
  teams: TeamSummary[];
  year: number;
}) {
  const compactChartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: { color: '#9ca3af', font: { size: 10 }, boxWidth: 8, padding: 6 },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: { color: 'rgba(255,255,255,0.06)' },
        ticks: { color: '#6b7280', font: { size: 9 } },
      },
      y1: {
        position: 'right' as const,
        beginAtZero: true,
        grid: { drawOnChartArea: false },
        ticks: { color: '#6b7280', font: { size: 9 } },
      },
      x: {
        grid: { display: false },
        ticks: { color: '#6b7280', font: { size: 9 }, maxRotation: 45 },
      },
    },
  }), []);

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Project header */}
      <div className="mb-4 flex items-baseline gap-4">
        <h1 className="text-3xl font-bold">
          {project.projectName} <span className="text-primary-500 text-xl ml-2">{year}</span>
        </h1>
        <div className="flex gap-6 text-sm">
          <div>
            <span className="text-gray-500">Costs </span>
            <span className="font-semibold text-white">
              {project.totalCosts.toLocaleString('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })}
            </span>
          </div>
          <div>
            <span className="text-gray-500">Revenue </span>
            <span className="font-semibold text-white">
              {project.totalRevenue.toLocaleString('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })}
            </span>
          </div>
          <div>
            <span className="text-gray-500">C/R </span>
            <span className="font-semibold" style={{ color: getCRRatioColor(project.costOverRevenue) }}>
              {project.costOverRevenue.toFixed(2)}
            </span>
          </div>
        </div>
      </div>

      {/* Team cards grid — 2x2 */}
      <div className="flex-1 grid grid-cols-2 grid-rows-2 gap-4 min-h-0">
        {teams.map((team) => (
          <div
            key={team.teamId}
            className="bg-dark-700/50 border border-dark-600 rounded-xl p-4 flex flex-col min-h-0"
          >
            <h3 className="font-semibold text-white text-base mb-2">{team.teamName}</h3>

            {/* Stats row */}
            <div className="grid grid-cols-3 gap-2 text-sm mb-3">
              <div>
                <p className="text-xs text-gray-500">Costs</p>
                <p className="font-medium text-white">
                  {team.totalCosts.toLocaleString('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Revenue</p>
                <p className="font-medium text-white">
                  {team.totalRevenue.toLocaleString('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500">C/R</p>
                <p className="font-medium" style={{ color: getCRRatioColor(team.costOverRevenue) }}>
                  {team.costOverRevenue.toFixed(2)}
                </p>
              </div>
            </div>

            {/* Compact chart */}
            {team.sprintData.length > 0 ? (
              <div className="flex-1 min-h-0">
                <Line
                  data={{
                    labels: team.sprintData.map((s) => s.sprintName),
                    datasets: [
                      {
                        label: 'Costs',
                        data: team.sprintData.map((s) => s.costs),
                        borderColor: '#E31837',
                        backgroundColor: 'rgba(227, 24, 55, 0.1)',
                        tension: 0.3,
                        pointRadius: 2,
                        borderWidth: 2,
                        yAxisID: 'y',
                      },
                      {
                        label: 'Revenue',
                        data: team.sprintData.map((s) => s.revenue),
                        borderColor: '#2DE2BF',
                        backgroundColor: 'rgba(45, 226, 191, 0.1)',
                        tension: 0.3,
                        pointRadius: 2,
                        borderWidth: 2,
                        yAxisID: 'y',
                      },
                      {
                        label: 'C/R',
                        data: team.sprintData.map((s) => s.costOverRevenue),
                        borderColor: '#0066FF',
                        backgroundColor: 'rgba(0, 102, 255, 0.1)',
                        tension: 0.3,
                        pointRadius: 2,
                        borderWidth: 2,
                        borderDash: [4, 3],
                        yAxisID: 'y1',
                      },
                    ],
                  }}
                  options={compactChartOptions}
                />
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center text-gray-600 text-sm">
                No sprint data
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
